import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import { ArrowLeft, Edit, Users, Bell, Filter } from 'lucide-react';
import SignalChart from '../components/SignalChart';
import SyncStatus from '../components/SyncStatus';
const PersonaDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'accounts' | 'users' | 'signals'>('accounts');
  // Mock persona data
  const persona = {
    id: parseInt(id || '1'),
    name: 'Enterprise Customers',
    type: 'Account',
    description: 'Accounts with over 100 users and high engagement',
    count: 12,
    createdAt: 'Jan 15, 2025',
    status: 'Active',
    filters: [{
      field: 'User Count',
      operator: 'greater than',
      value: '100'
    }, {
      field: 'MRR',
      operator: 'greater than',
      value: '$10,000'
    }, {
      field: 'Industry',
      operator: 'equals',
      value: 'Technology'
    }]
  };
  // Mock accounts data
  const accountColumns = [{
    key: 'name',
    label: 'Account Name',
    sortable: true
  }, {
    key: 'industry',
    label: 'Industry',
    sortable: true
  }, {
    key: 'users',
    label: 'Users',
    sortable: true
  }, {
    key: 'mrr',
    label: 'MRR',
    sortable: true
  }, {
    key: 'status',
    label: 'CRM Status',
    sortable: true,
    render: (value: string) => <SyncStatus status={value as 'Synced' | 'Not Synced'} data-id="element-1383" />
  }, {
    key: 'lastActivity',
    label: 'Last Activity',
    sortable: true
  }];
  const accountData = [{
    id: 1,
    name: 'Acme Corporation',
    industry: 'Technology',
    users: 125,
    mrr: '$12,500',
    status: 'Synced',
    lastActivity: '2 hours ago'
  }, {
    id: 3,
    name: 'Stark Industries',
    industry: 'Technology',
    users: 210,
    mrr: '$21,000',
    status: 'Synced',
    lastActivity: '5 hours ago'
  }, {
    id: 5,
    name: 'Globex Corporation',
    industry: 'Finance',
    users: 142,
    mrr: '$14,500',
    status: 'Synced',
    lastActivity: '12 hours ago'
  }, {
    id: 9,
    name: 'Massive Dynamic',
    industry: 'Research',
    users: 156,
    mrr: '$15,600',
    status: 'Not Synced',
    lastActivity: '8 hours ago'
  }, {
    id: 10,
    name: 'Hooli',
    industry: 'Technology',
    users: 187,
    mrr: '$18,700',
    status: 'Synced',
    lastActivity: '3 hours ago'
  }];
  // Mock users data
  const userColumns = [{
    key: 'name',
    label: 'Name',
    sortable: true
  }, {
    key: 'email',
    label: 'Email',
    sortable: true
  }, {
    key: 'account',
    label: 'Account',
    sortable: true
  }, {
    key: 'role',
    label: 'Role',
    sortable: true
  }, {
    key: 'lastActive',
    label: 'Last Active',
    sortable: true
  }, {
    key: 'sessions',
    label: 'Sessions',
    sortable: true
  }, {
    key: 'status',
    label: 'CRM Status',
    sortable: true,
    render: (value: string) => <SyncStatus status={value as 'Synced' | 'Not Synced'} data-id="element-1384" />
  }];
  const userData = [{
    id: 1,
    name: 'John Smith',
    email: 'john.smith@acme.com',
    account: 'Acme Corporation',
    role: 'Admin',
    lastActive: '2 hours ago',
    sessions: 128,
    status: 'Synced'
  }, {
    id: 3,
    name: 'Michael Brown',
    email: 'm.brown@stark.com',
    account: 'Stark Industries',
    role: 'Admin',
    lastActive: '3 hours ago',
    sessions: 87,
    status: 'Synced'
  }, {
    id: 5,
    name: 'David Wilson',
    email: 'd.wilson@globex.com',
    account: 'Globex Corporation',
    role: 'Manager',
    lastActive: '5 hours ago',
    sessions: 64,
    status: 'Synced'
  }, {
    id: 9,
    name: 'James Miller',
    email: 'j.miller@massive.com',
    account: 'Massive Dynamic',
    role: 'Admin',
    lastActive: '6 hours ago',
    sessions: 92,
    status: 'Not Synced'
  }, {
    id: 10,
    name: 'Lisa White',
    email: 'l.white@hooli.com',
    account: 'Hooli',
    role: 'Manager',
    lastActive: '4 hours ago',
    sessions: 76,
    status: 'Synced'
  }, {
    id: 11,
    name: 'Thomas Johnson',
    email: 't.johnson@acme.com',
    account: 'Acme Corporation',
    role: 'User',
    lastActive: '1 day ago',
    sessions: 45,
    status: 'Synced'
  }, {
    id: 12,
    name: 'Emma Davis',
    email: 'e.davis@stark.com',
    account: 'Stark Industries',
    role: 'User',
    lastActive: '8 hours ago',
    sessions: 38,
    status: 'Not Synced'
  }];
  // Updated signal chart data with realistic patterns and variations
  const signalChartData = [{
    id: 1,
    title: 'Average Login Frequency',
    type: 'bar',
    subtitle: 'Logins per Week',
    actionType: 'Authentication',
    timeSeriesData: [{
      date: 'Week 1',
      value: 15
    }, {
      date: 'Week 2',
      value: 18
    }, {
      date: 'Week 3',
      value: 22
    }, {
      date: 'Week 4',
      value: 25
    }, {
      date: 'Week 5',
      value: 23
    }, {
      date: 'Week 6',
      value: 27
    }, {
      date: 'Week 7',
      value: 29
    }, {
      date: 'Week 8',
      value: 32
    }],
    trend: 'up',
    percentage: 12,
    timeframe: 'vs last period'
  }, {
    id: 2,
    title: 'Feature Adoption Rate',
    type: 'line',
    subtitle: 'By Feature Category',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Jan',
      value: 42
    }, {
      date: 'Feb',
      value: 55
    }, {
      date: 'Mar',
      value: 65
    }, {
      date: 'Apr',
      value: 72
    }, {
      date: 'May',
      value: 80
    }, {
      date: 'Jun',
      value: 85
    }, {
      date: 'Jul',
      value: 88
    }, {
      date: 'Aug',
      value: 92
    }, {
      date: 'Sep',
      value: 95
    }],
    trend: 'up',
    percentage: 8,
    timeframe: 'vs last 6 months'
  }, {
    id: 3,
    title: 'User Activation',
    type: 'bar',
    subtitle: 'First Month Activity',
    actionType: 'User Flow',
    timeSeriesData: [{
      date: 'Week 1',
      value: 95
    }, {
      date: 'Week 2',
      value: 88
    }, {
      date: 'Week 3',
      value: 82
    }, {
      date: 'Week 4',
      value: 78
    }, {
      date: 'Week 5',
      value: 76
    }, {
      date: 'Week 6',
      value: 75
    }],
    trend: 'down',
    percentage: 4,
    timeframe: 'vs last cohort'
  }, {
    id: 4,
    title: 'MRR Growth',
    type: 'line',
    subtitle: 'Monthly Revenue',
    actionType: 'Business Metric',
    timeSeriesData: [{
      date: 'Jan',
      value: 10000
    }, {
      date: 'Feb',
      value: 11200
    }, {
      date: 'Mar',
      value: 12500
    }, {
      date: 'Apr',
      value: 13800
    }, {
      date: 'May',
      value: 15200
    }, {
      date: 'Jun',
      value: 16500
    }, {
      date: 'Jul',
      value: 18200
    }, {
      date: 'Aug',
      value: 19800
    }, {
      date: 'Sep',
      value: 21500
    }],
    trend: 'up',
    percentage: 18,
    timeframe: 'vs last 6 months'
  }, {
    id: 5,
    title: 'Support Tickets',
    type: 'bar',
    subtitle: 'By Category',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Technical',
      value: 28
    }, {
      date: 'Billing',
      value: 15
    }, {
      date: 'Feature',
      value: 22
    }, {
      date: 'Access',
      value: 12
    }, {
      date: 'Other',
      value: 8
    }, {
      date: 'Security',
      value: 5
    }],
    trend: 'down',
    percentage: 12,
    timeframe: 'vs last month'
  }, {
    id: 6,
    title: 'User Retention',
    type: 'line',
    subtitle: 'By Month',
    actionType: 'Business Metric',
    timeSeriesData: [{
      date: 'Month 1',
      value: 100
    }, {
      date: 'Month 2',
      value: 92
    }, {
      date: 'Month 3',
      value: 88
    }, {
      date: 'Month 4',
      value: 85
    }, {
      date: 'Month 5',
      value: 83
    }, {
      date: 'Month 6',
      value: 82
    }, {
      date: 'Month 7',
      value: 81
    }, {
      date: 'Month 8',
      value: 80
    }, {
      date: 'Month 9',
      value: 80
    }],
    trend: 'stable',
    percentage: 1,
    timeframe: 'vs last cohort'
  }];
  const handleAccountClick = (account: any) => {
    navigate(`/accounts/${account.id}`);
  };
  const handleUserClick = (user: any) => {
    navigate(`/users/${user.id}`);
  };
  return <div className="space-y-6" data-id="element-1385">
      <div className="flex items-center gap-4" data-id="element-1386">
        <button onClick={() => navigate('/personas')} className="p-2 rounded-full hover:bg-gray-100" data-id="element-1387">
          <ArrowLeft size={20} data-id="element-1388" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1389">{persona.name}</h1>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${persona.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1390">
          {persona.status}
        </span>
        <button className="ml-auto bg-[#0070d2] hover:bg-[#0057a3] text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-1391">
          <Edit size={16} data-id="element-1392" />
          <span data-id="element-1393">Edit Persona</span>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-1394">
        <div className="col-span-1 bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-1395">
          <div className="p-6" data-id="element-1396">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1397">
              Persona Information
            </h2>
            <div className="space-y-4" data-id="element-1398">
              <div data-id="element-1399">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-1400">Type</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-1401">{persona.type}</p>
              </div>
              <div data-id="element-1402">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-1403">
                  Description
                </h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-1404">
                  {persona.description}
                </p>
              </div>
              <div data-id="element-1405">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-1406">
                  Total Count
                </h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-1407">
                  {persona.count}{' '}
                  {persona.type === 'Account' ? 'Accounts' : 'Users'}
                </p>
              </div>
              <div data-id="element-1408">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-1409">Created</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-1410">
                  {persona.createdAt}
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1411">
              <div className="flex items-center justify-between mb-2" data-id="element-1412">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-1413">Filters</h3>
                <button className="text-xs text-[#0070d2] flex items-center gap-1" data-id="element-1414">
                  <Filter size={12} data-id="element-1415" />
                  <span data-id="element-1416">Edit Filters</span>
                </button>
              </div>
              <div className="mt-2 space-y-2" data-id="element-1417">
                {persona.filters.map((filter, index) => <div key={index} className="p-2 bg-gray-50 rounded-md border border-gray-200 text-sm" data-id="element-1418">
                    <span className="font-medium text-gray-700" data-id="element-1419">
                      {filter.field}
                    </span>
                    <span className="text-gray-500" data-id="element-1420"> {filter.operator} </span>
                    <span className="text-gray-900" data-id="element-1421">{filter.value}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-1422">
          <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-1423">
            <div className="border-b border-gray-200" data-id="element-1424">
              <nav className="flex -mb-px" data-id="element-1425">
                <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'accounts' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('accounts')} data-id="element-1426">
                  <div className="flex items-center gap-2" data-id="element-1427">
                    <Users size={18} data-id="element-1428" />
                    <span data-id="element-1429">Accounts</span>
                  </div>
                </button>
                <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'users' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('users')} data-id="element-1430">
                  <div className="flex items-center gap-2" data-id="element-1431">
                    <Users size={18} data-id="element-1432" />
                    <span data-id="element-1433">Users</span>
                  </div>
                </button>
                <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'signals' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('signals')} data-id="element-1434">
                  <div className="flex items-center gap-2" data-id="element-1435">
                    <Bell size={18} data-id="element-1436" />
                    <span data-id="element-1437">Signals</span>
                  </div>
                </button>
              </nav>
            </div>
            {activeTab === 'accounts' ? <DataTable columns={accountColumns} data={accountData} onRowClick={handleAccountClick} data-id="element-1438" /> : activeTab === 'users' ? <DataTable columns={userColumns} data={userData} onRowClick={handleUserClick} data-id="element-1439" /> : <div className="p-6" data-id="element-1440">
                <div className="grid grid-cols-2 gap-6" data-id="element-1441">
                  {signalChartData.map(chart => <SignalChart key={chart.id} data={chart} data-id="element-1442" />)}
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default PersonaDetail;